import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";

// Accepts a normalised job shape:
// { type, site_name, site_address, date, detail, status, raw: { site, scheduled_time, flexible, notes, ... } }
// Also accepts raw inspection objects from FieldTechDashboard (job.site instead of job.raw.site).
export default function SiteAccessPanel({ job, onClose, onStartWork }) {
  const [site, setSite] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    setSite(null);
    if (!job) return;
    const rawSite = job.raw?.site ?? job.site;
    const siteId = typeof rawSite === "object" ? rawSite?.id : rawSite;
    if (!siteId) return;
    axios
      .get(`${API}/sites/${siteId}/`)
      .then((res) => setSite(res.data))
      .catch(() => {});
  }, [job]);

  if (!job) return null;

  const isRectification = job.type === "rectification";
  const isInspection = !isRectification && (job.type || "inspection") !== "service_request";
  const typeTag   = isRectification ? "🔨 Rectification Work"
                  : isInspection    ? "📋 Inspection"
                  :                   "🔧 Service Request";
  const typeColor = isRectification ? colors.red
                  : isInspection    ? colors.teal
                  :                   colors.amber;

  // Date display — SRs have YYYY-MM-DD (date-only); append noon to avoid UTC-midnight shift
  const rawDate = job.date || job.raw?.scheduled_date;
  const dateDisplay = rawDate
    ? isInspection
      ? new Date(rawDate).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })
      : new Date(rawDate + "T12:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })
    : null;

  // Time display
  let timeDisplay = null;
  if (isInspection && rawDate) {
    timeDisplay = new Date(rawDate).toLocaleTimeString("en-AU", {
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } else {
    const st = job.raw?.scheduled_time;
    if (st) {
      // "HH:MM:SS" → 12-hr display
      const [h, m] = st.split(":").map(Number);
      const ampm = h >= 12 ? "pm" : "am";
      const h12 = h % 12 || 12;
      timeDisplay = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
    } else if (job.raw?.flexible) {
      timeDisplay = "do-anytime";
    }
  }

  const panelStyle = isMobile
    ? {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: colors.white,
        borderRadius: "16px 16px 0 0",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
        padding: "20px",
        zIndex: 300,
        maxHeight: "80vh",
        overflowY: "auto",
      }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: colors.white,
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        padding: "24px",
        zIndex: 300,
        width: "460px",
        maxHeight: "80vh",
        overflowY: "auto",
      };

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: `1px solid ${colors.border}`,
  };
  const rowLabel = {
    fontSize: "12px",
    fontWeight: "700",
    color: colors.slate,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };
  const rowValue = (highlight) => ({
    fontSize: "13px",
    color: highlight || colors.darkGrey,
    fontWeight: highlight ? "700" : "400",
    textAlign: "right",
    maxWidth: "65%",
  });

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          zIndex: 299,
        }}
      />
      <div style={panelStyle}>
        {isMobile && (
          <div
            style={{
              width: "40px",
              height: "4px",
              borderRadius: "2px",
              backgroundColor: colors.border,
              margin: "0 auto 16px",
            }}
          />
        )}

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: typeColor,
                marginBottom: "4px",
              }}
            >
              {typeTag}
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: colors.darkGrey }}>
              {job.site_name}
            </div>
            <div style={{ fontSize: "13px", color: colors.slate }}>
              {job.site_address}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              color: colors.slate,
            }}
          >
            ✕
          </button>
        </div>

        {/* Site photo */}
        {site?.street_view_photo_url && (
          <div style={{ marginBottom: "14px" }}>
            <img
              src={site.street_view_photo_url}
              alt="Site exterior"
              style={{
                width: "100%",
                maxHeight: "200px",
                objectFit: "cover",
                borderRadius: "8px",
                border: `1px solid ${colors.border}`,
              }}
            />
          </div>
        )}

        {/* Detail rows */}
        <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: "12px" }}>
          {dateDisplay && (
            <div style={rowStyle}>
              <span style={rowLabel}>Date</span>
              <span style={rowValue()}>{dateDisplay}</span>
            </div>
          )}

          {timeDisplay && (
            <div style={rowStyle}>
              <span style={rowLabel}>Time</span>
              {timeDisplay === "do-anytime" ? (
                <span style={{ ...rowValue(colors.amber) }}>Do anytime</span>
              ) : (
                <span style={rowValue()}>{timeDisplay}</span>
              )}
            </div>
          )}

          {isInspection && job.detail && (
            <div style={rowStyle}>
              <span style={rowLabel}>Type</span>
              <span style={rowValue()}>{job.detail}</span>
            </div>
          )}

          {isRectification && job.detail && (
            <div style={rowStyle}>
              <span style={rowLabel}>Work Items</span>
              <span style={rowValue()}>{job.detail}</span>
            </div>
          )}

          {!isInspection && !isRectification && job.detail && (
            <div style={rowStyle}>
              <span style={rowLabel}>Request</span>
              <span style={{ ...rowValue(), maxWidth: "70%", whiteSpace: "pre-wrap" }}>
                {job.detail}
              </span>
            </div>
          )}

          {job.status && (
            <div style={rowStyle}>
              <span style={rowLabel}>Status</span>
              <span
                style={rowValue(
                  job.status === "passed" || job.status === "complete" || job.status === "completed"
                    ? colors.green
                    : job.status === "failed"
                    ? colors.red
                    : job.status === "pending"
                    ? colors.amber
                    : colors.teal
                )}
              >
                {job.status === "passed" ? "Passed"
                  : job.status === "failed" ? "Failed"
                  : job.status === "complete" || job.status === "completed" ? "Complete"
                  : job.status === "pending" ? "Pending"
                  : "In Progress"}
              </span>
            </div>
          )}

          {(job.raw?.notes || job.notes) && (
            <div style={rowStyle}>
              <span style={rowLabel}>Notes</span>
              <span style={{ ...rowValue(), maxWidth: "70%", whiteSpace: "pre-wrap" }}>
                {job.raw?.notes || job.notes}
              </span>
            </div>
          )}
        </div>

        {/* Defects to rectify — only shown for rectification job type */}
        {isRectification && (() => {
          const items = job.raw?.quote_detail?.items || [];
          if (!items.length) return null;
          return (
            <div style={{ marginTop: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "10px" }}>
                🔨 Defects to Rectify
              </div>
              {items.map((qItem, i) => {
                const d = qItem.inspection_item_detail || {};
                const photos = [d.photo_1_url, d.photo_2_url, d.photo_3_url].filter(Boolean);
                return (
                  <div key={qItem.id} style={{ marginBottom: "12px", padding: "10px", border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: colors.darkGrey, marginBottom: "4px" }}>
                      {i + 1}. {d.asset_type_name || "Asset"}
                      {(d.floor_level || d.location) && (
                        <span style={{ fontWeight: "400", color: colors.slate }}>
                          {" — "}{[d.floor_level, d.location].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </div>
                    {d.notes && (
                      <div style={{ fontSize: "12px", color: colors.red, marginBottom: "6px" }}>
                        Defect: {d.notes}
                      </div>
                    )}
                    {photos.length > 0 && (
                      <div style={{ display: "flex", gap: "6px", marginBottom: "6px", flexWrap: "wrap" }}>
                        {photos.map((url, pi) => (
                          <img key={pi} src={url} alt={`defect photo ${pi + 1}`}
                            style={{ width: "80px", height: "60px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${colors.border}` }} />
                        ))}
                      </div>
                    )}
                    {qItem.proposed_solution && (
                      <div style={{ fontSize: "12px", color: colors.teal }}>
                        ✅ Recommended: {qItem.proposed_solution}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Site access details */}
        {site ? (
          <div style={{ marginTop: "12px" }}>
            {site.site_hazards && (
              <div
                style={{
                  backgroundColor: "#fdecea",
                  border: `1px solid ${colors.red}`,
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ fontSize: "12px", fontWeight: "700", color: colors.red, marginBottom: "4px" }}>
                  ⚠️ SITE HAZARDS
                </div>
                <div style={{ fontSize: "13px", color: colors.red }}>{site.site_hazards}</div>
              </div>
            )}
            {site.access_instructions && (
              <div style={{ padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", marginBottom: "4px" }}>
                  🔑 Access Instructions
                </div>
                <div style={{ fontSize: "13px", color: colors.darkGrey }}>{site.access_instructions}</div>
              </div>
            )}
            {site.parking_notes && (
              <div style={{ padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", marginBottom: "4px" }}>
                  🅿️ Parking
                </div>
                <div style={{ fontSize: "13px", color: colors.darkGrey }}>{site.parking_notes}</div>
              </div>
            )}
            {site.tools_required && (
              <div style={{ padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", marginBottom: "4px" }}>
                  🧰 Tools / Materials
                </div>
                <div style={{ fontSize: "13px", color: colors.darkGrey }}>{site.tools_required}</div>
              </div>
            )}
            {site.contacts?.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", marginBottom: "8px" }}>
                  📞 Site Contacts
                </div>
                {site.contacts.map((contact) => (
                  <div key={contact.id} style={{ padding: "8px 0", borderBottom: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: colors.darkGrey }}>{contact.name}</div>
                    {contact.role && <div style={{ fontSize: "12px", color: colors.slate }}>{contact.role}</div>}
                    {contact.phone && <div style={{ fontSize: "12px", color: colors.teal }}>📞 {contact.phone}</div>}
                    {contact.mobile && <div style={{ fontSize: "12px", color: colors.teal }}>📱 {contact.mobile}</div>}
                    {contact.email && <div style={{ fontSize: "12px", color: colors.slate }}>✉️ {contact.email}</div>}
                  </div>
                ))}
              </div>
            )}
            {!site.site_hazards && !site.access_instructions && !site.parking_notes && !site.tools_required && !site.contacts?.length && (
              <div style={{ textAlign: "center", padding: "16px", color: "#aaa", fontSize: "13px" }}>
                No site access details on file
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "16px", color: "#aaa", fontSize: "13px" }}>
            Loading site details...
          </div>
        )}
        {/* Start Work button — service requests and rectification work */}
        {(job.type === "service_request" || job.type === "rectification") && onStartWork && (
          <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: `1px solid ${colors.border}` }}>
            <button
              onClick={() => { onClose(); onStartWork(job); }}
              style={{
                width: "100%", padding: "13px", borderRadius: "8px", border: "none",
                backgroundColor: job.type === "rectification" ? colors.red : colors.amber,
                color: "white", fontSize: "14px", fontWeight: "700", cursor: "pointer",
              }}
            >
              {job.type === "rectification" ? "🔨 Start Rectification Work" : "🔧 Start Service Request"}
            </button>
          </div>
        )}

        <div style={{ height: "20px" }} />
      </div>
    </>
  );
}
